import {
  _StoreWithGetters,
  PiniaCustomProperties,
  _StoreWithState
} from 'pinia';
import { UnwrapRef } from 'vue';
import { UserStateInterface } from './state';
import { UserGetters } from './getters';

import { Credentials } from 'realm-web';

import { LocalStorage } from 'quasar';
import { CaptchaInfo, Option } from 'src/models/models';
import mixpanel from 'mixpanel-browser';
import { jwtDecode } from 'jwt-decode';

import {
  realmlogIn,
  realmLogInLink,
  realmCallResetPassword,
  realmResetPassword,
  realmLogOut,
  realmRegister,
  realmGetCaptcha,
  realmUser
} from 'src/services/functions';

type UserActions = {
  init(): void;
  logIn(credentials: Credentials): Promise<void>;
  logInLink(credentials: Credentials): Promise<void>;
  callResetPassword(email: string): Promise<void>;
  resetPassword(
    token: string,
    tokenId: string,
    password: string
  ): Promise<void>;
  logOut(): Promise<void>;
  register(payload: { email: string; password: string }): Promise<void>;
  getCaptcha(): Promise<CaptchaInfo>;
  setDistrict(value: Option[]): void;
  setCity(value: Option[]): void;
  setState(value: Option[]): void;
  addNext(value: string): void;
  removeNext(): void;
  setFirstTime(): void;
};

const actions: UserActions &
  ThisType<
    UserActions &
      UnwrapRef<UserStateInterface> &
      _StoreWithState<string, UserStateInterface, UserGetters, UserActions> &
      _StoreWithGetters<UserGetters> &
      PiniaCustomProperties
  > = {
  init() {
    const user = realmUser();
    if (user) {
      const { exp } = jwtDecode(user?.accessToken as string)
      if (exp && Date.now() >= exp * 1000) {
        user.refreshAccessToken().then(console.log).catch(() => {
          user.logOut().then(console.log).catch(console.error)
        });
      }
      this.identifier = user.id;
      this.type = user.identities.find(el =>
        ['local-userpass', 'custom-token'].includes(el.providerType)
      )
        ? 'local-userpass'
        : 'anon-user';
    }
  },
  async logIn(credentials: Credentials) {
    return realmlogIn(credentials).then(user => {
      this.identifier = user.id;
      this.type = user.identities.find(el =>
        ['local-userpass', 'custom-token'].includes(el.providerType)
      )
        ? 'local-userpass'
        : 'anon-user';
      mixpanel.identify(user.id);
    });
  },
  async logInLink(credentials: Credentials) {
    return realmLogInLink(credentials).then(user => {
      this.identifier = user.id;
      this.type = user.identities.find(
        el => el.providerType === 'local-userpass'
      )
        ? 'local-userpass'
        : 'anon-user';
    });
  },
  callResetPassword(email: string) {
    return realmCallResetPassword(email);
  },
  resetPassword(token: string, tokenId: string, password: string) {
    return realmResetPassword(token, tokenId, password);
  },
  async logOut() {
    return realmLogOut().then(() => {
      this.$reset();
      mixpanel.reset();
    });
  },
  async getCaptcha() {
    return realmGetCaptcha();
  },
  async register(payload: { email: string; password: string }) {
    return realmRegister(payload.email, payload.password).then(() => {
      mixpanel.track('User registered');
    });
  },
  setDistrict(value: Option[]) {
    this.district = value;
    LocalStorage.set('district', value);
  },
  setCity(value: Option[]) {
    this.city = value;
    LocalStorage.set('city', value);
  },
  setState(value: Option[]) {
    this.state = value;
    LocalStorage.set('state', value);
  },
  addNext(value: string) {
    this.next = value;
    LocalStorage.set('next', value);
  },
  removeNext() {
    this.next = undefined;
    LocalStorage.remove('next');
  },
  setFirstTime() {
    this.firstTime = false;
    LocalStorage.set('firstTime', false);
  }
};

export default actions;
